import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { translations } from '@/i18n/translations';
import { companies, getDataset } from '@/data/mockData';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('fp_lang') || 'es');
  const [theme, setTheme] = useState(() => localStorage.getItem('fp_theme') || 'dark');
  const [companyId, setCompanyId] = useState(() => localStorage.getItem('fp_company') || null);
  const [authed, setAuthed] = useState(() => localStorage.getItem('fp_authed') === '1');
  const [advisorOpen, setAdvisorOpen] = useState(false);

  useEffect(() => { localStorage.setItem('fp_lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('fp_theme', theme); }, [theme]);
  useEffect(() => { if (companyId) localStorage.setItem('fp_company', companyId); else localStorage.removeItem('fp_company'); }, [companyId]);
  useEffect(() => { localStorage.setItem('fp_authed', authed ? '1' : '0'); }, [authed]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.style.colorScheme = theme;
  }, [theme]);

  const t = useCallback((key) => translations[lang]?.[key] ?? key, [lang]);
  const tx = useCallback((obj) => {
    if (obj === null || obj === undefined) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] ?? obj.es ?? '';
  }, [lang]);

  const company = useMemo(() => companies.find((c) => c.id === companyId) || null, [companyId]);
  const data = useMemo(() => (companyId ? getDataset(companyId) : null), [companyId]);

  const value = {
    lang, setLang,
    theme, setTheme,
    companyId, setCompanyId,
    authed, setAuthed,
    advisorOpen, setAdvisorOpen,
    company, companies, data,
    t, tx,
    toggleLang: () => setLang((l) => (l === 'es' ? 'en' : 'es')),
    toggleTheme: () => setTheme((th) => (th === 'dark' ? 'light' : 'dark')),
    logout: () => { setAuthed(false); setCompanyId(null); },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
